/**
 * Innovation Hub area (/hub/...). The menu follows the Hub's own capabilities answer
 * (GET v3/hub/admin/capabilities): a page in the typed registry (platforms/adminNav.ts HUB_PAGES) is
 * listed when one of its actions is in the answer. 0.54.0 (Bill 2026-11-03): section pages show plain
 * page links only, like Retail and Loans; a capability action with no portal page is not shown.
 *
 * Redesign 0.53.0 (Bill 2026-11-03) — the home is the shared platform home:
 *   Needs attention  failed ask-card deliveries, GET v3/hub/admin/asks/deliveries?status=failed (when the
 *                    capability answer has asks.deliveries);
 *   Recent changes   the Hub's admin audit, GET v3/hub/admin/audit (when it has audit.list), reads left out.
 *
 * The Hub is called only with the shared backend token. Until BT grants consent the area says
 * so instead of calling (auth/tokens.ts).
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { EmptyState, PageHeader, PlatformHome, type AttentionItem, type RecentChange } from '@retail-ops/panel-shell';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { CONSENT_WAITING_MESSAGE } from '@/auth/tokens';
import { PlatformLayout } from '@/platforms/PlatformLayout';
import { SIX_SECTIONS } from '@/platforms/types';
import { Banner } from '@/components/primitives';
import { hubNav, hubPagesFor, hubTasksFor, useAdminShell } from '@/platforms/adminNav';
import { SectionCards, ShellLink, useSearchPick, whenText } from '@/platforms/homeShared';
import { SectionPageCards } from '@/platforms/retail/RetailArea';
import { hubApi } from './hubApi';

export const HUB_BASE = '/hub';

/** What an empty section page says (no routes, no verbs). */
export const EMPTY_SECTION_TEXT = 'Nothing to do here from the admin portal yet.';

export function hubSectionOf(pathname: string): string | null {
  const seg = pathname.slice(HUB_BASE.length + 1).split('/')[0] ?? '';
  return SIX_SECTIONS.some((s) => s.id === seg) ? seg : null;
}

export function ConsentWaiting({ what }: { what: string }): JSX.Element {
  return (
    <div>
      <PageHeader title={what} />
      <Banner tone="info">
        <strong>{CONSENT_WAITING_MESSAGE}.</strong> {what} accepts only the shared sign-in token, which this portal can
        get once Business Technology grants admin consent for it. Nothing is sent to {what} until then.
      </Banner>
    </div>
  );
}

export function HubArea(): JSX.Element {
  const { status } = usePlatforms();
  const st = status.hub;
  if (st.kind === 'loading') return <div className="loading-inline">Checking your Innovation Hub admin rights…</div>;
  if (st.kind === 'consent-waiting') return <ConsentWaiting what="Innovation Hub" />;
  if (st.kind === 'error') return <Banner tone="err">Could not reach the Innovation Hub: {st.message}</Banner>;
  if (st.kind !== 'ready') {
    return (
      <EmptyState title="You are not a Hub administrator.">
        Hub admin is the exact signed-in account listed in hub.admin.upns; an alias never counts, so an Azure Admin account must be
        listed itself.
      </EmptyState>
    );
  }
  return (
    <PlatformLayout
      title={st.caps.displayName ?? 'Innovation Hub'}
      basePath={HUB_BASE}
      enabled={new Set(st.caps.sections.map((s) => String(s.id)))}
      sectionOf={hubSectionOf}
    />
  );
}

export function HubHome(): JSX.Element {
  const { status } = usePlatforms();
  const st = status.hub;
  const { searchItems } = useAdminShell(HUB_BASE);
  const onPick = useSearchPick();
  const actions = st.kind === 'ready' ? st.caps.actions.map((a) => a.id) : [];
  const canDeliveries = actions.includes('asks.deliveries');
  const canAudit = actions.includes('audit.list');

  const [attention, setAttention] = useState<AttentionItem | null>(null);
  useEffect(() => {
    if (!canDeliveries) return undefined;
    let cancelled = false;
    const path = `${HUB_BASE}/monitoring/deliveries`;
    void hubApi
      .deliveries('failed')
      .then((r) => {
        if (cancelled) return;
        const failed = r.counts?.find((c) => c.deliveryStatus === 'failed');
        const total = failed?.total ?? r.deliveries.length;
        const onOpen = failed?.openAsks;
        setAttention(
          total === 0
            ? { id: 'deliveries', tone: 'ok', chip: 'All clear', title: 'Ask card deliveries', text: 'No ask cards have failed to deliver.', path }
            : {
                id: 'deliveries',
                tone: 'problem',
                chip: `${total} failed`,
                title: 'Ask cards that did not arrive',
                text: onOpen !== undefined ? `${onOpen} on asks that are still open.` : 'Open the list to see where each was going.',
                path,
              },
        );
      })
      .catch((e: unknown) => {
        if (!cancelled) setAttention({ id: 'deliveries', tone: 'neutral', chip: 'Could not check', title: 'Ask card deliveries', text: e instanceof Error ? e.message : String(e), path });
      });
    return () => {
      cancelled = true;
    };
  }, [canDeliveries]);

  const [recent, setRecent] = useState<{ loading: boolean; error: string | null; items: RecentChange[] }>({ loading: true, error: null, items: [] });
  useEffect(() => {
    if (!canAudit) return undefined;
    let cancelled = false;
    void hubApi
      .audit({ top: 50 })
      .then((r) => {
        if (cancelled) return;
        const writes = (r.entries ?? []).filter((e) => e.outcome !== 'read').slice(0, 5);
        setRecent({
          loading: false,
          error: null,
          // The audit keeps the call and its outcome (not a reason), so that is what is shown.
          items: writes.map((e) => ({ id: String(e.auditId), what: `${e.route}: ${e.outcome}`, who: e.actorUpn, when: whenText(e.createdAt) })),
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) setRecent({ loading: false, error: e instanceof Error ? e.message : String(e), items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [canAudit]);

  if (st.kind !== 'ready') return <></>;
  return (
    <PlatformHome
      title={`${st.caps.displayName ?? 'Innovation Hub'} administration`}
      intro="Hub settings, the admin audit and stuck card deliveries. Every write needs a reason and is recorded in the Hub's admin audit. The Hub dashboard keeps asks, support requests, backlog search, schedule and documents."
      Link={ShellLink}
      searchItems={searchItems}
      onSearchPick={onPick}
      attention={canDeliveries ? { loading: attention === null, items: attention ? [attention] : [] } : null}
      tasks={hubTasksFor(st.caps)}
      recent={canAudit ? { ...recent, seeAllPath: `${HUB_BASE}/monitoring/audit`, seeAllLabel: 'See the full admin audit' } : null}
      footer={<SectionCards nav={hubNav(st.caps)} />}
    />
  );
}

export function HubSectionPage(): JSX.Element {
  const { section } = useParams();
  const { status } = usePlatforms();
  const st = status.hub;
  const def = SIX_SECTIONS.find((s) => s.id === section);
  if (!def || st.kind !== 'ready') return <EmptyState title="There is no such section.">Choose a section from the menu.</EmptyState>;
  const pages = hubPagesFor(st.caps).filter((pg) => pg.section === def.id);
  return (
    <div>
      <PageHeader title={def.label} intro={def.holds} />
      {pages.length === 0 ? (
        <EmptyState title={EMPTY_SECTION_TEXT} />
      ) : (
        <SectionPageCards pages={pages.map((pg) => ({ path: pg.path, label: pg.label, group: pg.group ?? def.label, help: pg.help }))} />
      )}
    </div>
  );
}
